import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "moveload_growth_session";

function sessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "anon-fallback-session";
  }
}

function safeMetadata(meta: Record<string, any>): Record<string, any> {
  try {
    const json = JSON.stringify(meta ?? {});
    return json.length <= 1024 ? (meta ?? {}) : {};
  } catch {
    return {};
  }
}

type PendingEvent = {
  event_name: string;
  intent: string | null;
  session_id: string;
  path: string | null;
  referrer: string | null;
  metadata: Record<string, any>;
};

// Batch growth-event writes so high-traffic pages don't fire a DB insert per interaction.
const BATCH_MS = 2000;
const MAX_BATCH = 20;
let pending: PendingEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (pending.length === 0) return;
  const batch = pending.splice(0, MAX_BATCH);
  void supabase.from("growth_events").insert(batch).then(() => {}, () => {});
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, BATCH_MS);
}

/**
 * Fire a growth funnel event. Pushes into dataLayer for analytics AND
 * records to `growth_events` for the founder growth dashboard.
 * Failures are swallowed — telemetry must never break UX.
 *
 * Writes are batched (2s / 20 events) to protect the database under viral load.
 */
export function trackGrowth(event: string, payload: Record<string, any> = {}) {
  try {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ event, ...payload });
  } catch {}

  const { intent, ...rest } = payload as { intent?: string; [k: string]: any };
  pending.push({
    // Normalise to the server-side accepted shape: lowercase, [a-z0-9_.:-], 3-80 chars.
    event_name: (event.toLowerCase().replace(/[^a-z0-9_.:-]+/g, "_").slice(0, 80) || "unknown").padEnd(3, "_"),
    intent: intent ? String(intent).slice(0, 40) : null,
    session_id: sessionId(),
    path: typeof window !== "undefined" ? window.location.pathname.slice(0, 512) : null,
    referrer: typeof document !== "undefined" ? (document.referrer || "").slice(0, 512) || null : null,
    metadata: safeMetadata(rest),
  });


  if (pending.length >= MAX_BATCH) {
    flush();
  } else {
    scheduleFlush();
  }
}

// Ensure any queued events are sent before the page unloads.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", flush);
  window.addEventListener("pagehide", flush);
}
